/* Built on react-flatpickr v3.10.6 (uses flatpickr v4.6.2) */
import React, { useEffect, useRef, useState } from "react";

import { CloseOutlined } from "@ant-design/icons";
import { Button } from "antd";
import moment from "moment";
import Flatpickr from "react-flatpickr";

import MonthYearSelect from "./MonthYearSelect";

import { getCurrentYear } from "../../../utils/common";
import { convertUtcToLocal } from "../utils/dateUtils";
import { getSelectedDates } from "./utils";

import "flatpickr/dist/themes/material_green.css";
import "../../Product/Product.css";
import "./Calendar.css";
// Calculate the start and end years to be shown in the dropdown menu
// Also used to limit the min/max values a user can navigate to

const yearStart = 11;
const yearEnd = 0;
const currDate = moment(); // new Date();

const currYear = getCurrentYear();

// const start = new Date().getFullYear() - yearStart;
const defaultStart = moment().subtract(yearStart, "years").year();
const defaultEnd = currYear + yearEnd;
const defaultFormat = "m.d.Y";
const defaultMode = "range";

export function flatpickrToMomentFormat(flatpickrFormat) {
  const formatMap = {
    // Days
    d: "DD",
    D: "ddd",
    l: "dddd",

    // Months
    m: "MM",
    n: "M",
    M: "MMM",
    F: "MMMM",

    // Years
    y: "YY",
    Y: "YYYY",

    // Hours
    H: "HH",
    h: "hh",
    G: "H",
    g: "h",

    // Minutes / Seconds
    i: "mm",
    S: "ss",

    // AM/PM
    K: "A",
  };

  return flatpickrFormat.replace(/(d|D|l|m|n|M|F|y|Y|H|h|G|g|i|S|K)/g, (match) => formatMap[match] || match);
}

export default function Calendar({
  setShowCalendarInternal,
  handleSearchDate,
  handleCancel,
  setPrevDateSelected,
  getReportDates = async () => [], // default to async function returning empty array
  dateSelected,
  setDateSelected,
  searchOnClear,
  calendarClassName,
  disableSingleDaySelection,
  useLocalTime,
  showClearBtn,
  allowClear = true,
  mode,
  dateFormat,
  minDate,
  maxDate,
  allowFutureDateSelect,
}) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [flatpickrSelectedDates, setFlatpickrSelectedDates] = useState();
  const [currentSelectedMonth, setCurrentSelectedMonth] = useState(moment(currDate).month());
  const [currentSelectedYear, setCurrentSelectedYear] = useState(moment(currDate).year());
  const [reportDates, setReportDates] = useState(new Set());
  const [datesReceived, setDatesReceived] = useState(false);

  const momentFormat = flatpickrToMomentFormat(dateFormat || defaultFormat);
  const start = minDate === undefined ? defaultStart : moment(minDate || "", momentFormat).year();
  const end = maxDate === undefined ? defaultEnd : moment(maxDate || "", momentFormat).year();
  const getMinDate = () => {
    if (minDate !== undefined) {
      return minDate;
    }
    return moment().year(start).startOf("year").format("MM/DD/YYYY");
  };

  const getMaxDate = () => {
    if (maxDate !== undefined) {
      return maxDate;
    }
    if (allowFutureDateSelect) {
      return undefined;
    }
    return moment().year(end).endOf("year").format("MM/DD/YYYY");
  };

  const options = {
    plugins: [],
    inline: true,
    allowInput: true,
    dateFormat: dateFormat || defaultFormat,
    mode: mode || defaultMode,
    minDate: getMinDate(),
    maxDate: getMaxDate(),
  };

  const flatpickrRef = useRef();

  const apiGetReportDates = async () => {
    let dates = [];
    if (typeof getReportDates === "function") {
      dates = await getReportDates();
    }
    const datesSet = new Set();
    if (dates.length > 0) {
      dates.forEach((_date) => {
        let date = _date;
        if (useLocalTime) {
          date = convertUtcToLocal(_date);
        }
        const dateTemp = moment(date).format("LL");
        datesSet.add(dateTemp);
      });
    }
    setReportDates(datesSet);
    setDatesReceived(true);
  };

  const syncSelectionFromDateSelected = (selectedValue) => {
    const dates = getSelectedDates(selectedValue, false);

    if (dates !== undefined) {
      if (dates.length === 2) {
        const month = moment(dates[1]).month();
        const year = moment(dates[1]).year();
        setCurrentSelectedMonth(month);
        setCurrentSelectedYear(year);
        setStartDate(moment(dates[0]));
        setEndDate(moment(dates[1]));
      } else {
        const month = moment(dates[0]).month();
        const year = moment(dates[0]).year();
        setCurrentSelectedMonth(month);
        setCurrentSelectedYear(year);
        setStartDate(moment(dates[0]));
        setEndDate("");
      }
    } else {
      setStartDate("");
      setEndDate("");
      setCurrentSelectedYear(currYear);
    }

    const isCompleteRange = selectedValue.includes(" - ");
    const flatpickrDates = getSelectedDates(
      selectedValue,
      isCompleteRange || !disableSingleDaySelection,
    );
    setFlatpickrSelectedDates(flatpickrDates);
  };

  useEffect(() => {
    apiGetReportDates();
    syncSelectionFromDateSelected(dateSelected);
  }, []); // eslint-disable-line

  const handleCancelInternal = (e) => {
    e.stopPropagation();

    setStartDate("");
    setEndDate("");

    handleCancel();
    setShowCalendarInternal(false);
  };

  const onEndDateChange = async (date) => {
    if (date.length < 1) {
      return;
    }

    if (date[1] === undefined || moment(date[0]).valueOf() === moment(date[1]).valueOf()) {
      setStartDate(moment(date[0]));
      setEndDate("");
      const newDateSelected = moment(date[0].getTime()).format("MMM D, YYYY");
      setDateSelected(newDateSelected);
      const rangeSelectionComplete = disableSingleDaySelection ? false : date.length === 2;
      setFlatpickrSelectedDates(getSelectedDates(newDateSelected, rangeSelectionComplete));
    } else {
      setStartDate(moment(date[0]));
      setEndDate(moment(date[1]));
      const dateSelectedFrom = moment(date[0].getTime()).format("MMM D, YYYY");
      const dateSelectedTo = moment(date[1].getTime()).format("MMM D, YYYY");
      const newDateSelected = `${dateSelectedFrom} - ${dateSelectedTo}`;
      setDateSelected(newDateSelected);
      setFlatpickrSelectedDates(getSelectedDates(newDateSelected, true));
    }
  };

  const handleClearDateClick = () => {
    setStartDate("");
    setEndDate("");
    setDateSelected("");
    setPrevDateSelected("");
    setFlatpickrSelectedDates(getSelectedDates(""));
    if (flatpickrRef.current?.flatpickr) {
      flatpickrRef.current.flatpickr.clear();
    }
    setCurrentSelectedYear(currYear);
    if (searchOnClear) {
      handleSearchDate("", "");
      setShowCalendarInternal(false);
    }
  };

  const handleApplySearchFilter = () => {
    if (startDate) {
      const formatedDateStart = moment(startDate)
        .format("YYYY/MM/DD")
        .split("/")
        .join("-");
      const formatedDateEnd = endDate === ""
        ? endDate
        : moment(endDate).format("YYYY/MM/DD").split("/").join("-");

      handleSearchDate(formatedDateStart, formatedDateEnd);
      setStartDate("");
      setEndDate("");
      setPrevDateSelected(dateSelected);
      setShowCalendarInternal(false);
    }
  };

  /**
   * Update selected month in MonthYearSelect onMonthChange
   * @param {Array<Date>} selectedDates array of Date objects sorted from least to most recent
   * @param {String} dateStr date (06.04.2023 format) or date range (06.04.2023 to 07.03.2023 format)
   * @param {Flatpickr} instance flatpickr instance
   */
  const onMonthChange = (selectedDates, dateStr, instance) => {
    const month = instance.currentMonth;
    // const months = [
    //   "January", "February", "March", "April", "May", "June",
    //   "July", "August", "September", "October", "November", "December",
    // ];
    // console.log("onMonthChange", months[month]);
    setCurrentSelectedMonth(parseInt(month));
  };

  /**
   * Update selected year in MonthYearSelect onYearChange
   * @param {Array<Date>} selectedDates array of Date objects sorted from least to most recent
   * @param {String} dateStr date (06.04.2023 format) or date range (06.04.2023 to 07.03.2023 format)
   * @param {Flatpickr} instance flatpickr instance
   */
  const onYearChange = (selectedDates, dateStr, instance) => {
    const year = instance.currentYear;
    // console.log("onYearChange", year);
    setCurrentSelectedYear(parseInt(year));
  };

  /**
   * Update selectedDate, startDate, endDate onChange. If the current month or year shown in flatpickr
   * differs from what is shown in the MonthYearSelect, update MonthYearSelect.
   * @param {Array<Date>} selectedDates array of Date objects sorted from least to most recent
   */
  const onChange = (selectedDates) => {
    // console.log("onChange", selectedDates);
    onEndDateChange(selectedDates);
    /* There is a bug where onMonthChange, onYearChange aren't triggered when user clicks
    on a date from previous or next month and month/year changes.
    So if currentSelectedMonth and currentSelectedYear don't match the flatpickr end date month/year,
    update their values (on change, flatpickr always goes to the month of the most recent date)  */
    const fpCurrEndDate = selectedDates[selectedDates.length - 1]; // get most recent date in selection
    const fpCurrMonth = moment(fpCurrEndDate).month();
    const fpCurrYear = moment(fpCurrEndDate).year();
    if (fpCurrMonth !== currentSelectedMonth) {
      setCurrentSelectedMonth(fpCurrMonth);
    }
    if (fpCurrYear !== currentSelectedYear) {
      setCurrentSelectedYear(fpCurrYear);
    }
  };

  const disableApplyButton = startDate === "" || (disableSingleDaySelection && endDate === "");
  const hasClearableSelection = startDate !== "" || dateSelected !== "";

  return (
    <div className={`Calendar ${calendarClassName}`}>
      <div className="Calendar__Head">
        <div>Select Date</div>
        <CloseOutlined
          title="Close"
          onClick={handleCancelInternal}
          className="Calendar__CloseButton"
        />
      </div>
      <div className="Calendar__Body">
        <MonthYearSelect
          startYear={start}
          endYear={end}
          currentSelectedMonth={currentSelectedMonth}
          setCurrentSelectedMonth={setCurrentSelectedMonth}
          currentSelectedYear={currentSelectedYear}
          setCurrentSelectedYear={setCurrentSelectedYear}
          flatpickrRef={flatpickrRef}
        />
        <Flatpickr
          value={flatpickrSelectedDates}
          ref={flatpickrRef}
          options={{ ...options }}
          onChange={onChange}
          onDayCreate={(dObj, dStr, fp, dayElem) => {
            if (datesReceived) {
              if (
                reportDates.has(dayElem.getAttribute("aria-label"))
              ) {
                dayElem.innerHTML += "<span class='Calendar__MarkDay'></span>";
              }
            }
          }}
          onMonthChange={onMonthChange}
          onYearChange={onYearChange}
        />
        <div className={`Calendar__Actions ${showClearBtn ? "showClear" : "hideClear"}`}>
          {showClearBtn && (
          <Button
            type="link"
            disabled={!hasClearableSelection || !allowClear}
            onClick={!hasClearableSelection || !allowClear ? () => { } : handleClearDateClick}
          >
            Clear
          </Button>
          )}
          <Button
            onClick={disableApplyButton ? null : (e) => handleApplySearchFilter(e)}
            disabled={disableApplyButton}
            type="primary"
          >
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
}
